import { durationLabel } from './job-times'
import { roundClock, roundMoment, shownClock } from './round-config'
import type { JobTimeHow } from './types'
import { timeWheel } from './time-wheel'
import { el } from './ui'

// "When did you start cleaning?" and Change times (0083; Liam, 22 Oct 2026: "When I start the job and ... we're behind,
// the data's not gonna be clean ... it asks us when we started the job", then "Yes do it" to Astra's design,
// docs/plans/job-times-and-nudges/reviews/astra-late-start.md).
//
// Boxes drawn inside a job's card on Today, like Running late and Couldn't do it: nothing is saved until Save is pressed,
// and the caller saves it. One time box, already holding Roundly's best guess, to scroll and put right (Liam, on the first
// version's four tiles: "We don't need the four tiles ... we can just have one and we can make an educated guess based off
// the job time and the location to pre-fill the time ... give him a minute to get out of the car and start"):
// - the guess: the phone's noticed arrival and a minute to get started (leaving: a minute before it noticed you go);
//   else the job's booked time (a start) or its start and booked length (a finish); else now. The line under the box
//   says where it came from;
// - "Not sure" still starts or finishes the job, its time marked not known and left out of the customer's figures
//   (job-times.ts countsAsTimed, functions/lib/tiers.ts);
// - never a time later than now, never a finish before its start; a start before the job before it finished is said,
//   not refused (the worker may have forgotten to finish that one).

export interface TimeChoice { at: Date; how: JobTimeHow }

type Kind = 'start' | 'finish'

const WORDS: Record<Kind, { title: string; field: string; now: string; save: (t: string) => string }> = {
  start: { title: 'When did you start cleaning?', field: 'Start time', now: 'Starting now', save: (t) => `Started at ${t}` },
  finish: { title: 'When did you finish cleaning?', field: 'Finish time', now: 'Finishing now', save: (t) => `Finished at ${t}` },
}

// A phone's clock a little ahead of the server's is not "later than now".
const LEEWAY_MS = 60_000
// Out of the van and started, or packed up and back in it: a minute (Liam).
const GET_GOING_MS = 60_000
// A booked time is a guess at the start only while the day is still near it.
const BOOKED_GUESS_MS = 3 * 60 * 60_000

// Roundly's best guess at the time, and where it came from (said under the box).
export interface TimeGuess { at: Date; from: 'noticed' | 'booked' | 'length' | 'now'; words: string | null }

// "09:00", "9:30", or the start of a range ("09:00-11:30"), on the job's day; null for words ("morning").
function bookedMoment(day: string, slot: string | null | undefined): Date | null {
  const m = /^\s*(\d{1,2}):(\d{2})/.exec(slot ?? '')
  return m ? roundMoment(day, `${m[1]}:${m[2]}`) : null
}

export function guessStart(o: { day: string; noticed?: Date | null; booked?: string | null; finishedBefore?: Date | null; now: Date }): TimeGuess {
  const now = o.now.getTime()
  if (o.noticed) {
    return { at: new Date(Math.min(o.noticed.getTime() + GET_GOING_MS, now)), from: 'noticed',
      words: `Your phone noticed you arrive at ${shownClock(o.noticed)}. A minute added to get started.` }
  }
  const booked = bookedMoment(o.day, o.booked)
  if (booked && booked.getTime() <= now && now - booked.getTime() <= BOOKED_GUESS_MS && (!o.finishedBefore || booked.getTime() >= o.finishedBefore.getTime())) {
    return { at: booked, from: 'booked', words: `Booked for ${shownClock(booked)}.` }
  }
  return { at: o.now, from: 'now', words: null }
}

export function guessFinish(o: { noticed?: Date | null; startedAt?: Date | null; minutes?: number | null; now: Date }): TimeGuess {
  const now = o.now.getTime()
  const floor = o.startedAt?.getTime() ?? -Infinity
  if (o.noticed) {
    return { at: new Date(Math.min(Math.max(o.noticed.getTime() - GET_GOING_MS, floor), now)), from: 'noticed',
      words: `Your phone noticed you leave at ${shownClock(o.noticed)}. A minute taken off to pack up.` }
  }
  if (o.startedAt && o.minutes && o.minutes > 0) {
    const at = o.startedAt.getTime() + o.minutes * 60_000
    if (at <= now) return { at: new Date(at), from: 'length', words: `Started ${shownClock(o.startedAt)}, and the job is down as ${durationLabel(o.minutes)}.` }
  }
  return { at: o.now, from: 'now', words: null }
}

export interface AskOptions {
  kind: Kind
  // The job's day on the round's clock: the time in the box is on this day.
  day: string
  guess: TimeGuess
  // Starting now / Finishing now as well (Start job pressed, or the leaving question): the moment of the press.
  offerNow?: boolean
  // Nothing may be picked before this: a finish's own start.
  notBefore?: Date | null
  // Said, not refused, when a start is picked before it: the job before it finished then.
  warnBefore?: { at: Date; words: string } | null
  onPick: (c: TimeChoice) => void
  onBack: () => void
  backLabel?: string
  now?: () => Date
}

let hints = 0

// What is wrong with a time, or null; and a warning that does not stop it.
function judge(at: Date, now: Date, notBefore: Date | null | undefined, warnBefore: AskOptions['warnBefore'], kind: Kind): { stop: string | null; warn: string | null } {
  if (at.getTime() > now.getTime() + LEEWAY_MS) return { stop: "That's later than now.", warn: null }
  if (notBefore && at.getTime() < notBefore.getTime()) return { stop: `That's before you started (${shownClock(notBefore)}).`, warn: null }
  if (kind === 'start' && warnBefore && at.getTime() < warnBefore.at.getTime()) return { stop: null, warn: warnBefore.words }
  return { stop: null, warn: null }
}

export function timeChooser(o: AskOptions): HTMLElement {
  const now = o.now ?? ((): Date => new Date())
  const w = WORDS[o.kind]
  const box = el('div', { class: 'time-chooser', role: 'group', 'aria-label': w.title })
  box.append(el('p', { class: 'time-chooser-title' }, w.title))
  // The one box, holding the guess: scrolled to put it right (the phone's own time wheel).
  const typed = el('input', { type: 'time', class: 'time-chooser-input time-chooser-one', 'aria-label': w.field }) as HTMLInputElement
  const guessed = roundClock(o.guess.at)
  typed.value = guessed
  // The wheel showing at once, on the guess (Liam picked B, 26 Oct 2026): the box stays for a keyboard and a screen reader.
  box.classList.add('time-chooser-wheeled')
  const wheel = timeWheel(typed)
  box.append(typed, wheel.el)
  if (o.guess.words) {
    // Read out with the box, so a screen reader hears where the time came from before Save.
    const id = `time-hint-${++hints}`
    typed.setAttribute('aria-describedby', id)
    box.append(el('p', { class: 'time-chooser-hint', id }, o.guess.words))
  }
  const note = el('p', { class: 'time-chooser-note', 'aria-live': 'polite' })
  const save = el('button', { type: 'button', class: 'btn-primary time-chooser-save' })
  // The box holds minutes; a start has seconds. The minute it started in is never "before you started": it is the start.
  const typedAt = (): Date | null => {
    const at = roundMoment(o.day, typed.value)
    const floor = o.notBefore?.getTime()
    return at && floor !== undefined && at.getTime() < floor && floor - at.getTime() < 60_000 ? new Date(floor) : at
  }
  const paint = (): void => {
    const at = typedAt()
    const verdict = at ? judge(at, now(), o.notBefore, o.warnBefore, o.kind) : { stop: 'Put in a time.', warn: null }
    note.textContent = verdict.stop ?? verdict.warn ?? ''
    note.classList.toggle('time-chooser-warn', !verdict.stop && !!verdict.warn)
    save.disabled = !!verdict.stop
    save.textContent = at ? w.save(shownClock(at)) : 'Save'
  }
  typed.addEventListener('input', paint)
  typed.addEventListener('change', paint)
  save.addEventListener('click', () => {
    wheel.commit()
    paint()
    const at = typedAt()
    if (!at || save.disabled) return
    // Left as guessed: the phone's own time is the phone's ("noticed", counted); a guess from the booked time or length is
    // a plan, not a measure ("guessed", kept but left out of the figures: Astra's peer round); now is the time shown
    // when the box opened, saved as a press ("tap": the shown time, not the moment Save was pressed). Scrolled, it is
    // theirs ("said").
    const asGuessed = typed.value === guessed
    const how: JobTimeHow = !asGuessed ? 'said' : o.guess.from === 'noticed' ? 'noticed' : o.guess.from === 'now' ? 'tap' : 'guessed'
    o.onPick({ at: asGuessed ? o.guess.at : at, how })
  })
  box.append(note, save)
  const links = el('div', { class: 'time-chooser-links' })
  if (o.offerNow) {
    const nowLink = el('button', { type: 'button', class: 'time-chooser-link' }, w.now)
    nowLink.addEventListener('click', () => o.onPick({ at: now(), how: 'tap' }))
    links.append(nowLink)
  }
  const unsure = el('button', { type: 'button', class: 'time-chooser-link' }, 'Not sure')
  unsure.addEventListener('click', () => o.onPick({ at: now(), how: 'unsure' }))
  const back = el('button', { type: 'button', class: 'time-chooser-link' }, o.backLabel ?? 'Back')
  back.addEventListener('click', o.onBack)
  links.append(unsure, back)
  box.append(links)
  paint()
  return box
}

// ---- Change times: a job's start and finish put right afterwards --------------------------------------------------------

export interface ChangeTimesSave {
  started_at?: string
  started_how?: JobTimeHow
  finished_at?: string
  finished_how?: JobTimeHow
}

export interface ChangeTimesOptions {
  day: string
  started_at: string | null
  started_how: JobTimeHow | null
  // undefined: the job is still under way, so there is no finish to change.
  finished_at?: string | null
  finished_how?: JobTimeHow | null
  noticed?: { arrived: Date | null; left: Date | null }
  warnBefore?: AskOptions['warnBefore']
  onSave: (body: ChangeTimesSave) => void
  onBack: () => void
  now?: () => Date
}

const storedDate = (s: string | null | undefined): Date | null => (s ? new Date(`${s.replace(' ', 'T')}Z`) : null)

export function changeTimesChooser(o: ChangeTimesOptions): HTMLElement {
  const now = o.now ?? ((): Date => new Date())
  const withFinish = o.finished_at !== undefined
  const box = el('div', { class: 'time-chooser change-times', role: 'group', 'aria-label': 'Change times' })
  box.append(el('p', { class: 'time-chooser-title' }, withFinish ? 'Change times' : 'Change start time'))
  const was = { start: storedDate(o.started_at), finish: storedDate(o.finished_at) }
  // Filled from the phone's noticed time and not touched since: saved as the phone's (Astra: confirmed, so it counts).
  const fromPhone = { start: false, finish: false }
  const field = (which: 'start' | 'finish', label: string, noticed: Date | null | undefined): { input: HTMLInputElement; row: HTMLElement } => {
    const input = el('input', { type: 'time', class: 'time-chooser-input', 'aria-label': label }) as HTMLInputElement
    const had = was[which]
    if (had) input.value = roundClock(had)
    input.addEventListener('input', () => { fromPhone[which] = false; paint() })
    const row = el('div', { class: 'time-chooser-row' }, el('label', { class: 'time-chooser-field' }, el('span', {}, label), input))
    // Offered only when it says something the box does not already.
    if (noticed && input.value !== roundClock(noticed)) {
      const use = el('button', { type: 'button', class: 'time-chooser-use' }, `Use ${shownClock(noticed)} (your phone)`)
      use.addEventListener('click', () => { input.value = roundClock(noticed); fromPhone[which] = true; paint() })
      row.append(use)
    }
    return { input, row }
  }
  // The phone's times with the same minute as everywhere else: a minute after arriving, a minute before leaving.
  const start = field('start', 'Started', o.noticed?.arrived ? new Date(o.noticed.arrived.getTime() + GET_GOING_MS) : null)
  const finish = withFinish ? field('finish', 'Finished', o.noticed?.left ? new Date(o.noticed.left.getTime() - GET_GOING_MS) : null) : null
  box.append(start.row)
  if (finish) box.append(finish.row)
  const took = el('p', { class: 'time-chooser-took' })
  const wasRough = o.started_how === 'rough' || o.finished_how === 'rough'
  const rough = el('input', { type: 'checkbox' }) as HTMLInputElement
  rough.checked = wasRough
  rough.addEventListener('change', () => paint())
  const note = el('p', { class: 'time-chooser-note', 'aria-live': 'polite' })
  const save = el('button', { type: 'button', class: 'btn-primary time-chooser-save' }, 'Save times')
  box.append(took, el('label', { class: 'time-chooser-rough' }, rough, el('span', {}, 'These times are only rough')), note)

  const read = (input: HTMLInputElement | undefined): Date | null => (input?.value ? roundMoment(o.day, input.value) : null)
  const changed = (input: HTMLInputElement | undefined, had: Date | null): boolean => !!input?.value && (!had || roundClock(had) !== input.value)
  // The save as the boxes stand, or why not.
  const plan = (): { body: ChangeTimesSave; stop: string | null; warn: string | null } => {
    const body: ChangeTimesSave = {}
    const s = read(start.input) ?? was.start
    let f = finish ? read(finish.input) ?? was.finish : null
    if (!s) return { body, stop: f ? 'Put in when you started too.' : 'Put in when you started.', warn: null }
    // The boxes hold minutes and a kept start has seconds: a finish in the start's own minute is the start.
    if (f && f.getTime() < s.getTime() && s.getTime() - f.getTime() < 60_000) f = s
    if (s.getTime() > now().getTime() + LEEWAY_MS || (f && f.getTime() > now().getTime() + LEEWAY_MS)) return { body, stop: "That's later than now.", warn: null }
    if (f && f.getTime() < s.getTime()) return { body, stop: 'The finish is before the start.', warn: null }
    const howFor = (which: 'start' | 'finish', moved: boolean, had: JobTimeHow | null | undefined): JobTimeHow | undefined => {
      if (moved) return fromPhone[which] && !rough.checked ? 'noticed' : rough.checked ? 'rough' : 'said'
      // Not moved: only the rough tick changes how it was got.
      if (rough.checked && had !== 'rough') return 'rough'
      if (!rough.checked && had === 'rough') return 'said'
      return undefined
    }
    const sMoved = changed(start.input, was.start)
    const sHow = howFor('start', sMoved, o.started_how)
    if (sMoved) body.started_at = s.toISOString()
    if (sHow && (sMoved || was.start)) body.started_how = sHow
    if (finish && f) {
      const fMoved = changed(finish.input, was.finish)
      const fHow = howFor('finish', fMoved, o.finished_how)
      if (fMoved) body.finished_at = f.toISOString()
      if (fHow) body.finished_how = fHow
    }
    if (!Object.keys(body).length) return { body, stop: 'Nothing to change yet.', warn: null }
    const warn = o.warnBefore && sMoved && s.getTime() < o.warnBefore.at.getTime() ? o.warnBefore.words : null
    return { body, stop: null, warn }
  }
  function paint(): void {
    const s = read(start.input) ?? was.start
    const f = finish ? read(finish.input) ?? was.finish : null
    took.textContent = s && f && f.getTime() >= s.getTime() ? `Took ${durationLabel(Math.round((f.getTime() - s.getTime()) / 60_000))}` : ''
    const p = plan()
    // "Nothing to change yet" is not a fault to show before anything was touched.
    note.textContent = p.stop === 'Nothing to change yet.' ? '' : p.stop ?? p.warn ?? ''
    note.classList.toggle('time-chooser-warn', !p.stop && !!p.warn)
    save.disabled = !!p.stop
  }
  save.addEventListener('click', () => {
    const p = plan()
    if (!p.stop) o.onSave(p.body)
  })
  const actions = el('div', { class: 'time-chooser-actions' })
  const back = el('button', { type: 'button' }, 'Back')
  back.addEventListener('click', o.onBack)
  actions.append(back, save)
  // Times nobody can vouch for: kept, said so, and left out of the customer's figures.
  if (was.start) {
    const unknown = el('button', { type: 'button', class: 'time-chooser-unknown' }, "I don't know the times")
    unknown.addEventListener('click', () => {
      const body: ChangeTimesSave = { started_how: 'unsure' }
      if (finish && was.finish) body.finished_how = 'unsure'
      o.onSave(body)
    })
    box.append(unknown)
  }
  box.append(actions)
  paint()
  return box
}
